<script setup lang="ts">
import { computed, reactive, shallowRef, watch } from "vue"

import BatchItemPicker from "@/components/batches/BatchItemPicker.vue"
import BatchPhotoPicker from "@/components/batches/BatchPhotoPicker.vue"
import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import type { BatchItemFields } from "@/composables/useBatchMutations"
import { ICON } from "@/lib/icons"
import type { CaptureGroup } from "@/types/capture"

interface Option {
  id: string
  name: string
}

interface ContainerOption {
  label: string
  value: string
}

const props = defineProps<{
  group: CaptureGroup
  itemTypes: Option[]
  containers: ContainerOption[]
  defaultContainerId: string | null
  pendingAction: string | null
  error: string | null
  mergeCandidates: CaptureGroup[]
}>()

const emit = defineEmits<{
  create: [fields: BatchItemFields]
  attach: [itemId: string]
  analyze: []
  rotate: [degrees?: number]
  skip: []
  deletePhoto: [photoId: string, complete: (success: boolean) => void]
  splitGroup: [photoIds: string[], complete: (success: boolean) => void]
  mergeGroups: [sourceGroupIds: string[], complete: (success: boolean) => void]
}>()

const suggestedRotation = computed(() => props.group.analysis_data.suggested_rotation ?? 0)
const suggestedPhotoRotationCount = computed(
  () =>
    Object.values(props.group.analysis_data.suggested_photo_rotations ?? {}).filter(
      (degrees) => degrees !== 0,
    ).length,
)
const hasRotationSuggestion = computed(
  () => suggestedPhotoRotationCount.value > 0 || suggestedRotation.value !== 0,
)

function confirmRotation() {
  if (suggestedPhotoRotationCount.value > 0) {
    emit("rotate")
  } else {
    emit("rotate", suggestedRotation.value)
  }
}

function analysisFields(group: CaptureGroup) {
  return {
    name: group.analysis_data.name ?? "",
    description: group.analysis_data.description ?? "",
    condition: group.analysis_data.condition ?? "",
    quantity: group.analysis_data.quantity ?? 1,
    model_number: group.analysis_data.model_number ?? "",
    serial_number: group.analysis_data.serial_number ?? "",
    item_type_id: group.analysis_data.item_type_id ?? "",
  }
}

const form = reactive({
  ...analysisFields(props.group),
  parent_id: props.defaultContainerId ?? "",
})
const skipModalOpen = shallowRef(false)
const deleteModalOpen = shallowRef(false)
const mergeModalOpen = shallowRef(false)
const selectedPhotoIds = shallowRef<string[]>([])
const selectedMergeGroupIds = shallowRef<string[]>([])
const disabled = computed(() => props.pendingAction !== null)
const canDeletePhoto = computed(() => selectedPhotoIds.value.length === 1)
const canSplitPhotos = computed(
  () =>
    selectedPhotoIds.value.length > 0 && selectedPhotoIds.value.length < props.group.photos.length,
)

watch(
  () => props.group.analysis_generation,
  () => {
    // Only refresh the form when a genuinely new analysis has landed. Delete,
    // split, and "Analyze again" all bump the generation while the group is
    // re-queued with empty or stale data — overwriting the form there would
    // wipe the user's in-progress edits.
    if (props.group.analysis_status !== "succeeded") return
    if (!props.group.analysis_data.name) return
    Object.assign(form, analysisFields(props.group))
  },
)

function submit(isContainer: boolean) {
  emit("create", {
    ...form,
    quantity: Number(form.quantity) || 1,
    item_type_id: form.item_type_id || null,
    parent_id: form.parent_id || null,
    is_container: isContainer,
  })
}

function confirmSkip() {
  skipModalOpen.value = false
  emit("skip")
}

function confirmDeletePhoto() {
  const photoId = selectedPhotoIds.value[0]
  if (!photoId) return
  emit("deletePhoto", photoId, (success) => {
    if (!success) return
    selectedPhotoIds.value = []
    deleteModalOpen.value = false
  })
}

function splitSelectedPhotos() {
  if (!canSplitPhotos.value) return
  emit("splitGroup", selectedPhotoIds.value, (success) => {
    if (success) selectedPhotoIds.value = []
  })
}

function confirmMergeGroups() {
  if (selectedMergeGroupIds.value.length === 0) return
  emit("mergeGroups", selectedMergeGroupIds.value, (success) => {
    if (!success) return
    selectedMergeGroupIds.value = []
    mergeModalOpen.value = false
  })
}
</script>

<template>
  <div class="space-y-5">
    <p v-if="error" class="text-error text-sm" role="alert">{{ error }}</p>

    <section class="space-y-3" aria-labelledby="batch-photos-heading">
      <div>
        <h3 id="batch-photos-heading" class="text-highlighted text-sm font-semibold">
          Group photos
        </h3>
        <p class="text-muted text-sm">Select photos to remove or move into a new item group.</p>
      </div>
      <div
        v-if="hasRotationSuggestion"
        data-testid="rotation-suggestion"
        class="border-warning/40 bg-warning/10 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2"
      >
        <p class="text-highlighted text-sm">
          <template v-if="suggestedPhotoRotationCount">
            {{ suggestedPhotoRotationCount }}
            {{ suggestedPhotoRotationCount === 1 ? "photo looks" : "photos look" }} rotated. Rotate
            {{ suggestedPhotoRotationCount === 1 ? "it" : "them" }} upright?
          </template>
          <template v-else>
            These photos look rotated {{ suggestedRotation }}°. Rotate them upright?
          </template>
        </p>
        <button
          type="button"
          data-testid="rotate-photos"
          :disabled="disabled"
          class="border-default bg-default inline-flex min-h-11 items-center gap-2 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
          @click="confirmRotation"
        >
          <UIcon :name="ICON.rotate" class="size-4" />
          <template v-if="suggestedPhotoRotationCount">
            Rotate {{ suggestedPhotoRotationCount === 1 ? "photo" : "photos" }}
          </template>
          <template v-else>Rotate {{ suggestedRotation }}°</template>
        </button>
      </div>
      <BatchPhotoPicker v-model="selectedPhotoIds" :photos="group.photos" :disabled="disabled" />
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="delete-selected-photo"
          :disabled="disabled || !canDeletePhoto"
          class="text-error border-error/40 min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
          @click="deleteModalOpen = true"
        >
          Delete selected photo
        </button>
        <button
          type="button"
          data-testid="split-selected-photos"
          :disabled="disabled || !canSplitPhotos"
          class="border-default min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
          @click="splitSelectedPhotos"
        >
          Move selected to new group
        </button>
      </div>
    </section>

    <div class="grid gap-3 sm:grid-cols-2">
      <label class="text-highlighted block text-sm font-medium sm:col-span-2">
        Name
        <input
          v-model="form.name"
          name="name"
          required
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        />
      </label>
      <label class="text-highlighted block text-sm font-medium sm:col-span-2">
        Description
        <textarea
          v-model="form.description"
          name="description"
          rows="2"
          :disabled="disabled"
          class="border-default bg-default mt-1 w-full rounded-md border px-3 py-2 font-normal"
        />
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Type
        <select
          v-model="form.item_type_id"
          name="item_type_id"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        >
          <option value="">No type</option>
          <option v-for="itemType in itemTypes" :key="itemType.id" :value="itemType.id">
            {{ itemType.name }}
          </option>
        </select>
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Quantity
        <input
          v-model.number="form.quantity"
          name="quantity"
          type="number"
          min="1"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        />
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Condition
        <input
          v-model="form.condition"
          name="condition"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        />
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Container
        <select
          v-model="form.parent_id"
          name="parent_id"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        >
          <option value="">No container</option>
          <option v-for="container in containers" :key="container.value" :value="container.value">
            {{ container.label }}
          </option>
        </select>
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Model number
        <input
          v-model="form.model_number"
          name="model_number"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        />
      </label>
      <label class="text-highlighted block text-sm font-medium">
        Serial number
        <input
          v-model="form.serial_number"
          name="serial_number"
          :disabled="disabled"
          class="border-default bg-default mt-1 min-h-11 w-full rounded-md border px-3 font-normal"
        />
      </label>
    </div>

    <div class="grid gap-2 sm:grid-cols-2">
      <button
        type="button"
        data-testid="create-item"
        :disabled="disabled || !form.name.trim()"
        class="bg-primary text-inverted min-h-11 rounded-md px-4 text-sm font-medium disabled:opacity-50"
        @click="submit(false)"
      >
        Create item
      </button>
      <button
        type="button"
        data-testid="create-container"
        :disabled="disabled || !form.name.trim()"
        class="border-default min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
        @click="submit(true)"
      >
        Create as container
      </button>
    </div>

    <BatchItemPicker :disabled="disabled" @select="emit('attach', $event)" />

    <section v-if="mergeCandidates.length" class="border-default space-y-3 border-t pt-4">
      <div>
        <h3 class="text-highlighted text-sm font-semibold">Combine with another group</h3>
        <p class="text-muted text-sm">Use this when separate groups are photos of the same item.</p>
      </div>
      <div class="space-y-2">
        <label
          v-for="candidate in mergeCandidates"
          :key="candidate.id"
          :data-merge-group-id="candidate.id"
          class="border-default flex min-h-11 cursor-pointer items-center gap-3 rounded-md border px-3 py-2"
        >
          <input
            v-model="selectedMergeGroupIds"
            type="checkbox"
            :value="candidate.id"
            :disabled="disabled"
            class="size-5"
          />
          <span class="text-sm">
            Group {{ candidate.position }} ·
            {{ candidate.analysis_data.name || `${candidate.actual_photo_count} photos` }}
          </span>
        </label>
      </div>
      <button
        type="button"
        data-testid="open-merge-groups"
        :disabled="disabled || selectedMergeGroupIds.length === 0"
        class="border-default min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
        @click="mergeModalOpen = true"
      >
        Combine selected groups
      </button>
    </section>

    <div class="border-default flex flex-wrap gap-2 border-t pt-4">
      <button
        type="button"
        data-testid="analyze-group"
        :disabled="disabled"
        class="border-default min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
        @click="emit('analyze')"
      >
        Analyze again
      </button>
      <button
        type="button"
        data-testid="open-skip"
        :disabled="disabled"
        class="text-error border-error/40 min-h-11 rounded-md border px-4 text-sm font-medium disabled:opacity-50"
        @click="skipModalOpen = true"
      >
        Skip group
      </button>
    </div>

    <DeleteConfirmModal
      v-model:open="deleteModalOpen"
      title="Delete this photo?"
      action-label="Delete photo"
      :action-icon="ICON.delete"
      :loading="pendingAction === 'delete-photo'"
      @confirm="confirmDeletePhoto"
    >
      This permanently removes the selected photo from the capture batch.
    </DeleteConfirmModal>

    <DeleteConfirmModal
      v-model:open="mergeModalOpen"
      title="Merge these capture groups?"
      action-label="Merge groups"
      action-color="warning"
      :action-icon="ICON.arrowRight"
      :loading="pendingAction === 'merge'"
      @confirm="confirmMergeGroups"
    >
      All selected photos will become part of this item group. This cannot be undone here.
    </DeleteConfirmModal>

    <DeleteConfirmModal
      v-model:open="skipModalOpen"
      title="Skip this capture group?"
      action-label="Skip group"
      action-color="warning"
      :action-icon="ICON.arrowRight"
      :loading="pendingAction === 'skip'"
      @confirm="confirmSkip"
    >
      The photos stay in the batch, but this group will not create or attach an inventory item.
    </DeleteConfirmModal>
  </div>
</template>
